import { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import {
  getFeatureIcon,
  FEATURE_CATEGORIES,
  getFeatureCatalogGrouped,
} from '@/data/featureCatalog';
import type { FeatureCategory, FeatureCatalogEntry } from '@/data/featureCatalog';

interface FeaturesMegaMenuProps {
  useDarkText: boolean;
  onNavigate?: () => void;
}

function FeatureGroup({
  category,
  features,
  onNavigate,
}: {
  category: FeatureCategory;
  features: FeatureCatalogEntry[];
  onNavigate?: () => void;
}) {
  const { label } = FEATURE_CATEGORIES[category];

  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-2">
        {label}
      </h3>
      <ul className="space-y-0.5">
        {features.map((feature) => {
          const Icon = getFeatureIcon(feature.icon);
          return (
            <li key={feature.slug}>
              <Link
                to={`/features/${feature.slug}`}
                onClick={onNavigate}
                className="flex items-start gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
              >
                <Icon className="w-4 h-4 mt-0.5 text-gray-400 dark:text-gray-500 group-hover:text-primary-600 dark:group-hover:text-primary-400 flex-shrink-0 transition-colors" />
                <div className="min-w-0">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {feature.title}
                  </span>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-1">
                    {feature.shortDescription}
                  </p>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function FeaturesMegaMenu({ useDarkText, onNavigate }: FeaturesMegaMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const grouped = useMemo(() => getFeatureCatalogGrouped(), []);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Clean up timer
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const handleNavigate = () => {
    setIsOpen(false);
    onNavigate?.();
  };

  return (
    <div
      ref={menuRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${
          useDarkText
            ? 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
            : 'text-white/80 hover:text-white'
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        Features
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 w-[680px] max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-[51] overflow-hidden"
          role="menu"
        >
          <div className="grid grid-cols-2 gap-6 p-5">
            {/* Left column: Delivery + Quality */}
            <div className="space-y-5">
              <FeatureGroup category="delivery" features={grouped.delivery} onNavigate={handleNavigate} />
              <FeatureGroup category="quality" features={grouped.quality} onNavigate={handleNavigate} />
            </div>

            {/* Right column: Team + Intelligence */}
            <div className="space-y-5">
              <FeatureGroup category="team" features={grouped.team} onNavigate={handleNavigate} />
              <FeatureGroup category="intelligence" features={grouped.intelligence} onNavigate={handleNavigate} />
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-100 dark:border-gray-700 px-5 py-3 bg-gray-50 dark:bg-gray-900/50">
            <Link
              to="/features"
              onClick={handleNavigate}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              See all features
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
